import { ChangeEvent, useState } from "react";
import type { KeyedMutator } from "swr";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUploadAndUpdateMasterSpecList } from "@/hooks/useSpecLists";
import { RawSpecList, SpecList } from "@/types/specList";

type UploadNewVersionFormProps = {
  specList: SpecList;
  onOpenChange: (open: boolean) => void;
  onBack: () => void;
  refetch: KeyedMutator<RawSpecList>;
};

export const UploadNewVersionForm = ({
  specList,
  onOpenChange,
  onBack,
  refetch,
}: UploadNewVersionFormProps) => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const { trigger: updateMasterSpecList } = useUploadAndUpdateMasterSpecList();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;

    try {
      setIsUploading(true);
      await updateMasterSpecList(
        {
          file,
          spec_list_id: specList.id,
        },
        {
          onSuccess: async () => {
            toast.success("Successfully uploaded and created a new version!");
            onOpenChange(false);
            const updatedData = await refetch();
            if (updatedData) {
              const latestVersion = updatedData.versions.at(-1);
              if (latestVersion) {
                navigate(`/spec-lists/${specList.id}/version/${latestVersion.id}`);
              }
            }
          },
          onError: (error) => {
            toast.error(error.message);
          },
        },
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        <div>
          <Label htmlFor="newVersionFile">Master Spec File (.xls, .xlsx)</Label>
          <Input
            accept=".xls,.xlsx"
            disabled={isUploading}
            id="newVersionFile"
            onChange={handleFileChange}
            type="file"
            className="mt-2"
          />
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} disabled={isUploading}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!file || isUploading}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
};
